// Pure client-side reductions over event-scoped reports_transactions rows.
// Hasura has no group-by, so every tendency on the page is computed here from
// one skinny row pull. Kept free of React so the logic is testable directly.

export type TxnRow = {
  transaction_hour: string | null;
  transaction_amount: number | null; // cents
  tip: number | null; // cents (mirrors orders.tip)
  payment_type: string | null;
  vendor_name: string | null;
  location_name: string | null;
  device_app_id: string | null;
  order_items_count: number | null;
};

export type HourBucket = {
  key: string; // sortable local-time key
  label: string;
  revenue: number;
  txns: number;
};

export type VendorStat = {
  vendor: string;
  revenue: number;
  txns: number;
  items: number;
  tip: number;
  avgTicket: number;
  itemsPerTxn: number;
  tipRate: number; // tip / (revenue) — revenue here excludes tip? see note below
  share: number;
  locations: number;
  devices: number;
  peakHourLabel: string;
  peakHourRevenue: number;
  peakTxnsPerDeviceHour: number;
};

export type TenderStat = {
  type: string;
  revenue: number;
  txns: number;
  share: number;
};

export type Callout = {
  kind: "peak" | "concentration" | "ticket" | "registers" | "tips" | "basket";
  text: string;
};

export type EventInsights = {
  totals: {
    revenue: number;
    txns: number;
    tip: number;
    items: number;
    avgTicket: number;
    itemsPerTxn: number;
    tipRate: number;
  };
  tenderMix: TenderStat[];
  timeline: HourBucket[]; // every active hour of the event, in order
  hourOfDay: HourBucket[]; // all days folded into 24 local-time slots
  vendors: VendorStat[];
  callouts: Callout[];
};

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const hourLabel = (h: number) => {
  const ampm = h < 12 ? "am" : "pm";
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}${ampm}`;
};

// transaction_hour is a UTC timestamp; events store a fixed offset in
// timezone_diff (e.g. EDT = -4). Shifting then reading UTC getters yields
// event-local wall time without needing IANA zone names.
const toLocal = (iso: string, tzDiffHours: number): Date =>
  new Date(new Date(iso).getTime() + tzDiffHours * 3_600_000);

export const reduceInsights = (
  rows: TxnRow[],
  tzDiffHours: number
): EventInsights => {
  type VendorAcc = {
    revenue: number;
    txns: number;
    items: number;
    tip: number;
    locations: Set<string>;
    devices: Set<string>;
    byHour: Map<string, { label: string; revenue: number; txns: number; devices: Set<string> }>;
  };
  const vendorMap = new Map<string, VendorAcc>();
  const timelineMap = new Map<string, HourBucket>();
  const hodMap = new Map<number, HourBucket>();
  const tenderMap = new Map<string, { revenue: number; txns: number }>();

  let revenue = 0,
    txns = 0,
    tip = 0,
    items = 0;

  for (const r of rows) {
    // transaction_amount is the full tender charge: subtotal + tax + tip +
    // digital surcharge (verified against orders). Tips are stripped from the
    // revenue basis so high-tip vendors don't read as high-sellers; they get
    // their own column/KPI instead.
    const amount = (r.transaction_amount ?? 0) - (r.tip ?? 0);
    revenue += amount;
    txns++;
    tip += r.tip ?? 0;
    items += r.order_items_count ?? 0;

    const tenderKey = (r.payment_type ?? "(none)").trim() || "(none)";
    const tender = tenderMap.get(tenderKey) ?? { revenue: 0, txns: 0 };
    tender.revenue += amount;
    tender.txns++;
    tenderMap.set(tenderKey, tender);

    let hourKey = "";
    let hourFull = "";
    let hod = -1;
    if (r.transaction_hour) {
      const local = toLocal(r.transaction_hour, tzDiffHours);
      hod = local.getUTCHours();
      hourKey = local.toISOString().slice(0, 13); // YYYY-MM-DDTHH
      hourFull = `${DOW[local.getUTCDay()]} ${local.getUTCMonth() + 1}/${local.getUTCDate()} ${hourLabel(hod)}`;

      const tl = timelineMap.get(hourKey) ?? {
        key: hourKey,
        label: hourFull,
        revenue: 0,
        txns: 0,
      };
      tl.revenue += amount;
      tl.txns++;
      timelineMap.set(hourKey, tl);

      const hb = hodMap.get(hod) ?? {
        key: String(hod).padStart(2, "0"),
        label: hourLabel(hod),
        revenue: 0,
        txns: 0,
      };
      hb.revenue += amount;
      hb.txns++;
      hodMap.set(hod, hb);
    }

    const vendorKey = (r.vendor_name ?? "").trim() || "(no vendor)";
    const v =
      vendorMap.get(vendorKey) ??
      ({
        revenue: 0,
        txns: 0,
        items: 0,
        tip: 0,
        locations: new Set<string>(),
        devices: new Set<string>(),
        byHour: new Map(),
      } as VendorAcc);
    v.revenue += amount;
    v.txns++;
    v.items += r.order_items_count ?? 0;
    v.tip += r.tip ?? 0;
    if (r.location_name) v.locations.add(r.location_name);
    if (r.device_app_id) v.devices.add(r.device_app_id);
    if (hourKey) {
      const vh = v.byHour.get(hourKey) ?? {
        label: hourFull,
        revenue: 0,
        txns: 0,
        devices: new Set<string>(),
      };
      vh.revenue += amount;
      vh.txns++;
      if (r.device_app_id) vh.devices.add(r.device_app_id);
      v.byHour.set(hourKey, vh);
    }
    vendorMap.set(vendorKey, v);
  }

  const vendors: VendorStat[] = Array.from(vendorMap.entries())
    .map(([vendor, v]) => {
      let peakHourLabel = "—",
        peakHourRevenue = 0,
        peakTxnsPerDeviceHour = 0;
      for (const vh of v.byHour.values()) {
        if (vh.revenue > peakHourRevenue) {
          peakHourRevenue = vh.revenue;
          peakHourLabel = vh.label;
        }
        const perDevice = vh.txns / Math.max(1, vh.devices.size);
        if (perDevice > peakTxnsPerDeviceHour) peakTxnsPerDeviceHour = perDevice;
      }
      return {
        vendor,
        revenue: v.revenue,
        txns: v.txns,
        items: v.items,
        tip: v.tip,
        avgTicket: v.txns > 0 ? v.revenue / v.txns : 0,
        itemsPerTxn: v.txns > 0 ? v.items / v.txns : 0,
        tipRate: v.revenue > 0 ? v.tip / v.revenue : 0,
        share: revenue > 0 ? v.revenue / revenue : 0,
        locations: v.locations.size,
        devices: v.devices.size,
        peakHourLabel,
        peakHourRevenue,
        peakTxnsPerDeviceHour,
      };
    })
    .sort((a, b) => b.revenue - a.revenue);

  const timeline = Array.from(timelineMap.values()).sort((a, b) =>
    a.key.localeCompare(b.key)
  );
  const hourOfDay = Array.from(hodMap.values()).sort((a, b) =>
    a.key.localeCompare(b.key)
  );
  const tenderMix: TenderStat[] = Array.from(tenderMap.entries())
    .map(([type, t]) => ({
      type,
      revenue: t.revenue,
      txns: t.txns,
      share: revenue > 0 ? t.revenue / revenue : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const totals = {
    revenue,
    txns,
    tip,
    items,
    avgTicket: txns > 0 ? revenue / txns : 0,
    itemsPerTxn: txns > 0 ? items / txns : 0,
    tipRate: revenue > 0 ? tip / revenue : 0,
  };

  return {
    totals,
    tenderMix,
    timeline,
    hourOfDay,
    vendors,
    callouts: buildCallouts({ totals, timeline, vendors }),
  };
};

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const pct = (f: number) => `${Math.round(f * 100)}%`;

// Heuristic observations — each one is a tendency a festival can act on.
// Thresholds are deliberately conservative so an empty list means "nothing
// stands out", not "nothing was checked".
const buildCallouts = (d: {
  totals: EventInsights["totals"];
  timeline: HourBucket[];
  vendors: VendorStat[];
}): Callout[] => {
  const out: Callout[] = [];
  const { totals, timeline, vendors } = d;
  const realVendors = vendors.filter((v) => v.vendor !== "(no vendor)");

  if (timeline.length >= 6 && totals.revenue > 0) {
    const top3 = [...timeline]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 3);
    const top3Share = top3.reduce((s, h) => s + h.revenue, 0) / totals.revenue;
    out.push({
      kind: "peak",
      text: `Peak hour is ${top3[0].label} (${money(top3[0].revenue)}). The top 3 hours carry ${pct(top3Share)} of all revenue${
        top3Share > 0.35
          ? " — staffing and register counts should be built around that window"
          : ""
      }.`,
    });
  }

  if (realVendors.length >= 5 && totals.revenue > 0) {
    const topN = Math.max(1, Math.ceil(realVendors.length * 0.2));
    const topShare =
      realVendors.slice(0, topN).reduce((s, v) => s + v.revenue, 0) /
      totals.revenue;
    if (topShare > 0.55) {
      out.push({
        kind: "concentration",
        text: `${topN} of ${realVendors.length} vendors generate ${pct(topShare)} of sales — the long tail may need better placement, menu, or hours.`,
      });
    }
  }

  if (realVendors.length >= 3) {
    const tickets = realVendors
      .filter((v) => v.txns >= 20)
      .map((v) => v.avgTicket)
      .sort((a, b) => a - b);
    if (tickets.length >= 3) {
      const median = tickets[Math.floor(tickets.length / 2)];
      // Vendors >5× the median avg ticket are a different business type
      // (ticketing, merch bundles) — not an upsell lesson for food & bev.
      const candidates = realVendors.filter(
        (v) => v.txns >= 20 && v.avgTicket <= median * 5
      );
      const best = candidates.reduce(
        (a, b) => (b.avgTicket > a.avgTicket ? b : a),
        candidates[0]
      );
      if (best && median > 0 && best.avgTicket > median * 1.8) {
        out.push({
          kind: "ticket",
          text: `${best.vendor} averages ${money(best.avgTicket)} per sale vs an event median of ${money(median)} — bundling/upsell tactics there may transfer to other vendors.`,
        });
      }
    }
  }

  const pressured = realVendors
    .filter((v) => v.devices > 0 && v.peakTxnsPerDeviceHour >= 50)
    .slice(0, 3);
  if (pressured.length > 0) {
    out.push({
      kind: "registers",
      text: `${pressured
        .map(
          (v) =>
            `${v.vendor} (${Math.round(v.peakTxnsPerDeviceHour)} sales/register in its peak hour)`
        )
        .join(", ")} ${pressured.length === 1 ? "is" : "are"} running near register capacity — an extra device at peak likely unlocks sales.`,
    });
  }

  if (totals.tip > 0) {
    const tippers = realVendors.filter((v) => v.txns >= 20);
    const noTip = tippers.filter((v) => v.tip === 0);
    if (noTip.length > 0 && noTip.length < tippers.length) {
      out.push({
        kind: "tips",
        text: `${noTip.length} of ${tippers.length} vendors collected zero tips while others did — worth checking whether tip prompts are enabled on their devices.`,
      });
    }
  }

  if (totals.txns >= 200 && totals.itemsPerTxn > 0 && totals.itemsPerTxn < 1.3) {
    out.push({
      kind: "basket",
      text: `Orders average just ${totals.itemsPerTxn.toFixed(2)} items — mostly single-item sales. Combo pricing or menu bundles could lift ticket size.`,
    });
  }

  return out;
};
